// src/components/trip/StorageUsageCard.tsx
import React from 'react';
import { View, Text, Flex, Card } from '@aws-amplify/ui-react';
import { formatFileSize, calculatePercentage } from '../../utils/formatters';

interface StorageUsageCardProps {
  storageUsed: number;
  photoCount: number;
}

const StorageUsageCard: React.FC<StorageUsageCardProps> = ({
  storageUsed,
  photoCount
}) => {
  // Get color based on usage percentage
  const getProgressColor = (percent: number) => {
    if (percent > 90) return "#FF4B4B"; // Red
    if (percent > 75) return "#FFAA00"; // Orange
    return "#00C48C"; // Green
  };

  // Calculate average photo size
  const averagePhotoSize = photoCount > 0 ? storageUsed / photoCount : 0;
  
  // Set percentage relative to a maximum (like 10GB)
  // This is just for visualization purposes
  const percentUsed = calculatePercentage(storageUsed, 10 * 1024 * 1024 * 1024);
  const progressColor = getProgressColor(percentUsed);

  return (
    <Card
      backgroundColor="#1A1A1A"
      padding="1.5rem"
      borderRadius="medium"
      marginBottom="2rem"
    >
      <Flex direction="column" gap="1rem">
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontWeight="bold" fontSize="1.2rem">Total Trip Storage</Text>
          <Text fontWeight="bold" color={progressColor}>
            {formatFileSize(storageUsed)}
          </Text>
        </Flex>

        {/* Storage Progress Bar */}
        <View>
          <Flex justifyContent="space-between" marginBottom="0.5rem">
            <Text fontSize="0.9rem" color="#AAAAAA">Used: {formatFileSize(storageUsed)}</Text>
            <Text fontSize="0.9rem" color="#AAAAAA">{percentUsed.toFixed(1)}%</Text>
          </Flex>
          <View
            backgroundColor="#333"
            height="0.75rem"
            borderRadius="full"
            overflow="hidden"
          >
            <View
              backgroundColor={progressColor}
              height="100%"
              width={`${Math.min(100, percentUsed)}%`}
            />
          </View>
        </View>

        {/* Storage Usage Information */}
        <View backgroundColor="#202020" padding="1rem" borderRadius="medium" marginTop="0.5rem">
          <Flex direction="column" gap="0.5rem">
            <Flex justifyContent="space-between">
              <Text fontSize="0.9rem">Photos Count:</Text>
              <Text fontSize="0.9rem">{photoCount}</Text>
            </Flex>
            <Flex justifyContent="space-between">
              <Text fontSize="0.9rem">Average Size:</Text>
              <Text fontSize="0.9rem">
                {photoCount > 0 ? formatFileSize(averagePhotoSize) : "0 B"}
              </Text>
            </Flex>
          </Flex>
        </View>
      </Flex>
    </Card>
  );
};

export default StorageUsageCard;